import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { AlertTriangle, Coins, ImageIcon, Users, Wand2 } from 'lucide-react'
import { api } from '@/lib/api'
import type { AdminStats } from '@/lib/types'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { PageHeader } from '@/components/AppLayout'
import { formatMicros, formatNumber } from '@/lib/utils'

export function DashboardPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['stats'],
    queryFn: () => api.get<AdminStats>('api/admin/stats'),
    // Queue depth and failure counts are the reason to have this screen open,
    // so it stays current without a manual refresh.
    refetchInterval: 30_000,
  })

  return (
    <>
      <PageHeader title="Dashboard" description="Platform health, spend and activity." />

      <div className="space-y-6 p-8">
        {error && <ErrorNote message={(error as Error).message} />}

        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Stat icon={Users} label="Users" value={data?.totalUsers} loading={isLoading}
                sub={data ? `${formatNumber(data.newUsersLast7Days)} new this week` : undefined} />
          <Stat icon={Coins} label="Active subscriptions" value={data?.activeSubscriptions} loading={isLoading}
                sub={data ? `${formatNumber(data.creditsOutstanding)} credits outstanding` : undefined} />
          <Stat icon={ImageIcon} label="Wallpapers" value={data?.totalWallpapers} loading={isLoading}
                sub={data ? `${formatNumber(data.curatedWallpapers)} curated` : undefined} />
          <Stat icon={Wand2} label="Generations in flight" value={(data?.jobsQueued ?? 0) + (data?.jobsRunning ?? 0)}
                loading={isLoading}
                sub={data ? `${formatNumber(data.jobsQueued)} queued · ${formatNumber(data.jobsRunning)} running` : undefined} />
        </section>

        <section className="grid gap-4 lg:grid-cols-3">
          <Card>
            <CardHeader>
              <CardTitle>Model spend, last 30 days</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="tabular text-2xl font-semibold">
                {formatMicros(data?.generationCostMicrosLast30Days)}
              </p>
              <p className="mt-1 text-xs text-[var(--color-text-tertiary)]">
                Priced per image from the provider's reported token usage.{' '}
                <Link to="/costs" className="underline underline-offset-2">
                  Cost per image
                </Link>
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Credits spent, last 30 days</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="tabular text-2xl font-semibold">{formatNumber(data?.creditsSpentLast30Days)}</p>
              <p className="mt-1 text-xs text-[var(--color-text-tertiary)]">
                Net of refunds for failed generations.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Needs attention</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <AttentionRow
                label="Failed generations (24h)"
                value={data?.jobsFailedLast24h ?? 0}
                tone={(data?.jobsFailedLast24h ?? 0) > 0 ? 'warning' : 'success'}
              />
              <AttentionRow
                label="Unprocessed webhook events"
                value={data?.unprocessedWebhookEvents ?? 0}
                // An unprocessed subscription event means somebody paid and may
                // not have received what they paid for.
                tone={(data?.unprocessedWebhookEvents ?? 0) > 0 ? 'danger' : 'success'}
              />
              <AttentionRow
                label="Disabled accounts"
                value={data?.disabledUsers ?? 0}
                tone="default"
              />
            </CardContent>
          </Card>
        </section>

        {data && data.topFailuresLast7Days.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Top generation failures, last 7 days</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {data.topFailuresLast7Days.map((failure) => (
                <div key={failure.errorCode} className="flex items-center justify-between text-sm">
                  <code className="text-[var(--color-text-secondary)]">{failure.errorCode}</code>
                  <span className="tabular">{formatNumber(failure.count)}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </>
  )
}

function Stat({
  icon: Icon,
  label,
  value,
  sub,
  loading,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: number | undefined
  sub?: string
  loading: boolean
}) {
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle>{label}</CardTitle>
        <Icon className="size-4 text-[var(--color-text-tertiary)]" />
      </CardHeader>
      <CardContent>
        <p className="tabular text-2xl font-semibold">{loading ? '—' : formatNumber(value)}</p>
        {sub && <p className="mt-1 text-xs text-[var(--color-text-tertiary)]">{sub}</p>}
      </CardContent>
    </Card>
  )
}

function AttentionRow({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone: 'default' | 'success' | 'warning' | 'danger'
}) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-[var(--color-text-secondary)]">{label}</span>
      <Badge variant={tone}>{formatNumber(value)}</Badge>
    </div>
  )
}

export function ErrorNote({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-3 rounded-[var(--radius-card)] border border-[var(--color-danger)]/40 bg-[var(--color-danger)]/10 p-4 text-sm">
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-[var(--color-danger)]" />
      <span>{message}</span>
    </div>
  )
}
